/**
 * The support service's Reports API (support#87), as Support > Reports reads it.
 *
 * Its own file rather than more of `support-api.ts`: reports are not tickets
 * (Jon, 2026-10-24), and the shapes here mirror `routes/admin-reports.ts` in
 * the support repo, whose `docs/reports.md` is the contract. Same transport as
 * the queue: a support-audience bearer, never the cookie.
 */
import type { TokenSource } from './fleet-api.js';
import { SUPPORT_URL, SupportError } from './support-api.js';

export type ReportCategory =
  | 'CHILD_SAFETY'
  | 'VIOLENCE'
  | 'SEXUAL_CONTENT'
  | 'HARASSMENT'
  | 'PERSONAL_INFO'
  | 'IMPERSONATION'
  | 'SPAM'
  | 'OTHER';
export type ReportState = 'NEW' | 'ESCALATED' | 'ARCHIVED';
export type ReportTarget = 'PERSON' | 'NOTE' | 'PHOTO' | 'GROUP';
export type EscalationReason = 'REPORT_TO_AUTHORITIES' | 'PERMANENT_BAN_REVIEW';
export type EscalationStatus = 'ESCALATED' | 'PENDING_DECISION' | 'AWAITING_AUTHORITIES' | 'SECURED' | 'CLOSED';

/** In order, as the status control shows them (Jon, 2026-10-24). */
export const ESCALATION_STATUSES: readonly EscalationStatus[] = ['ESCALATED', 'PENDING_DECISION', 'AWAITING_AUTHORITIES', 'SECURED', 'CLOSED'];

export const STATUS_LABELS: Record<EscalationStatus, string> = {
  ESCALATED: 'Escalated',
  PENDING_DECISION: 'Pending decision',
  AWAITING_AUTHORITIES: 'Awaiting authorities',
  SECURED: 'Secured',
  CLOSED: 'Closed',
};

/** The two statuses that put a report under a legal hold (support's `LEGAL_HOLD`). */
export const HOLD_STATUSES: readonly EscalationStatus[] = ['AWAITING_AUTHORITIES', 'SECURED'];

/** Worst first: the order New groups by. Mirrors support's `REPORT_CATEGORY_ORDER`. */
export const REPORT_CATEGORIES: readonly ReportCategory[] = [
  'CHILD_SAFETY',
  'VIOLENCE',
  'SEXUAL_CONTENT',
  'HARASSMENT',
  'PERSONAL_INFO',
  'IMPERSONATION',
  'SPAM',
  'OTHER',
];

export const CATEGORY_LABELS: Record<ReportCategory, string> = {
  CHILD_SAFETY: 'Child safety',
  VIOLENCE: 'Violence or threats',
  SEXUAL_CONTENT: 'Sexual content',
  HARASSMENT: 'Harassment or hate',
  PERSONAL_INFO: 'Personal information',
  IMPERSONATION: 'Impersonation',
  SPAM: 'Spam or scam',
  OTHER: 'Other',
};

export const TARGET_LABELS: Record<ReportTarget, string> = {
  PERSON: 'Person',
  NOTE: 'Note',
  PHOTO: 'Photo',
  GROUP: 'Group',
};

export const ESCALATION_LABELS: Record<EscalationReason, string> = {
  REPORT_TO_AUTHORITIES: 'Report to authorities',
  PERMANENT_BAN_REVIEW: 'Permanent ban review',
};

export interface ReportRow {
  id: string;
  product: string;
  targetKind: ReportTarget;
  reasonId: string;
  category: ReportCategory;
  state: ReportState;
  reporterUserId: string;
  reportedUserId: string | null;
  reportedAt: string;
  viewedAt: string | null;
  escalatedAt: string | null;
  escalationReason: EscalationReason | null;
  escalationStatus: EscalationStatus | null;
  legalHold: boolean;
  escalationViewedAt: string | null;
  archivedAt: string | null;
  detailsPreview: string | null;
  contentHidden: boolean;
  temporaryBanUntil: string | null;
  permanentlyBannedAt: string | null;
  authoritiesReportedAt: string | null;
}

export interface ReportEvent {
  id: string;
  kind: string;
  detail: Record<string, unknown> | null;
  actorUserId: string | null;
  actorIsAgent: boolean;
  at: string;
}

export interface ReportNote {
  id: string;
  authorUserId: string;
  body: string;
  at: string;
}

/** One piece of evidence captured from the app. Never a link: images open through `openEvidence`. */
export interface ReportEvidence {
  id: string;
  kind: 'CONTENT' | 'PERSON' | 'IMAGE';
  role: 'reported' | 'avatar' | null;
  text: string | null;
  data: Record<string, unknown> | null;
  contentType: string | null;
  bytes: number | null;
  sha256: string | null;
  capturedAt: string;
  destroyedAt: string | null;
  destroyedByUserId: string | null;
  destroyReference: string | null;
}

export interface ReportDetail extends ReportRow {
  history: { reporterFiled: number; reportedAgainst: number; reportedAgainstActive: number };
  refs: Record<string, string>;
  contentSnapshot: string | null;
  reportedEventAt: string | null;
  details: string | null;
  alsoBlocked: boolean;
  reporterOrigin: { ip: string | null; country: string | null; region: string | null; city: string | null };
  escalatedByUserId: string | null;
  contentHiddenAt: string | null;
  authoritiesReference: string | null;
  securedAt: string | null;
  securedReference: string | null;
  snapshotAt: string | null;
  evidence: ReportEvidence[];
  archivedByUserId: string | null;
  events: ReportEvent[];
  notes: ReportNote[];
}

export interface ReportCounts {
  new: number;
  escalated: number;
  active: number;
  unviewedChildSafety: { new: number; escalated: number };
}

export interface ReportOverview {
  counts: ReportCounts;
  byCategory: { category: ReportCategory; new: number; escalated: number; oldestWaitingAt: string | null; archived30Days: number }[];
  days: { day: string; total: number; childSafety: number }[];
  medianToFirstView: number | null;
  medianToArchive: number | null;
  oldestNew: { id: string; reportedAt: string } | null;
  repeatReported: { userId: string; active: number }[];
  outcomes30Days: {
    archived: number;
    contentRemoved: number;
    temporaryBans: number;
    permanentBans: number;
    reportedToAuthorities: number;
  };
}

export interface ReportPage {
  state: ReportState;
  hasMore: boolean;
  nextCursor: string | null;
  reports: ReportRow[];
}

export type ReportOutcome = 'content-removed' | 'temporary-ban' | 'permanent-ban' | 'authorities';
export type ReportListState = 'new' | 'escalated' | 'archived' | 'all';

/** The filters every Overview figure opens a list with. */
export interface ReportFilter {
  category?: ReportCategory;
  outcome?: ReportOutcome;
  day?: string;
  archivedWithin?: number;
}

export function filterSearch(filter: ReportFilter): string {
  const q = new URLSearchParams();
  if (filter.category) q.set('category', filter.category);
  if (filter.outcome) q.set('outcome', filter.outcome);
  if (filter.day) q.set('day', filter.day);
  if (filter.archivedWithin) q.set('archivedWithin', String(filter.archivedWithin));
  return q.toString();
}

export type BanRecord = { kind: 'temporary'; reason: string; days: number } | { kind: 'permanent'; reason: string };

export function reportsApi(token: TokenSource, fetchImpl: typeof fetch = fetch) {
  async function call<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
    const res = await fetchImpl(`${SUPPORT_URL}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${await token()}`,
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      credentials: 'omit',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    if (!res.ok) {
      const parsed = (await res.json().catch(() => null)) as { error?: { code?: string; message?: string } } | null;
      throw new SupportError(res.status, parsed?.error?.message || `The support service answered ${res.status}.`, parsed?.error?.code);
    }
    return (await res.json()) as T;
  }
  const one = (id: string) => `/admin/reports/${encodeURIComponent(id)}`;
  const act = (id: string, path: string, body: unknown = {}) => call<ReportDetail>('POST', `${one(id)}/${path}`, body);

  return {
    counts: () => call<ReportCounts>('GET', '/admin/reports/counts'),
    overview: () => call<ReportOverview>('GET', '/admin/reports/overview'),
    list: (state: ReportListState, filter: ReportFilter = {}, cursor?: string) => {
      const extra = filterSearch(filter);
      return call<ReportPage>(
        'GET',
        `/admin/reports?state=${state}${extra ? `&${extra}` : ''}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
      );
    },
    report: (id: string) => call<ReportDetail>('GET', one(id)),
    viewed: (id: string) => act(id, 'viewed'),
    hideContent: (id: string) => act(id, 'content/hide'),
    restoreContent: (id: string) => act(id, 'content/restore'),
    captureEvidence: (id: string) => act(id, 'evidence/capture'),
    openEvidence: (id: string, evidenceId: string, reason?: string) =>
      call<{ url: string; expiresAt: string; sha256: string | null; contentType: string | null }>(
        'POST',
        `${one(id)}/evidence/${encodeURIComponent(evidenceId)}/open`,
        reason ? { reason } : {},
      ),
    destroyEvidence: (id: string, requestReference: string, confirmId: string) =>
      act(id, 'evidence/destroy', { requestReference, confirmId }),
    setStatus: (id: string, status: EscalationStatus, extra: { reference?: string; note?: string } = {}) =>
      act(id, 'status', { status, ...extra }),
    recordBan: (id: string, ban: BanRecord) => act(id, 'bans', ban),
    escalate: (id: string, reason: EscalationReason, note?: string) => act(id, 'escalate', { reason, ...(note ? { note } : {}) }),
    archive: (id: string, note?: string) => act(id, 'archive', note ? { note } : {}),
    addNote: (id: string, body: string) => act(id, 'notes', { body }),
  };
}

export type ReportsApi = ReturnType<typeof reportsApi>;
